import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { AssetIcon } from '@Components/shared/images/AssetIcon';
import { Button, Popover, Stack, Typography, useTheme } from '@mui/material';
import { TextField } from '@mui/material';

type Props = {
  title: string;
  onApply: (startDate?: string | null, endDate?: string | null) => void;
  onReset: () => void;
};

export const DateTableColumnHeader: React.FC<Props> = ({ title, onApply, onReset }) => {
  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = useState<string | null>('');
  const [endDate, setEndDate] = useState<string | null>('');
  const anchorRef = useRef<HTMLDivElement>(null);
  const { palette } = useTheme();

  useEffect(() => {
    setStartDate('');
    setEndDate('');
  }, []);

  const handleStartDateChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setStartDate(event.target.value || '');
    },
    [setStartDate]
  );

  const handleEndDateChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setEndDate(event.target.value || '');
    },
    [setEndDate]
  );

  const handleClick = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleApplyClick = useCallback(() => {
    const convertedStartDate = startDate ? new Date(startDate).toISOString() : undefined;
    const convertedEndDate = endDate ? new Date(endDate).toISOString() : undefined;
    onApply(convertedStartDate, convertedEndDate);
  }, [onApply, startDate, endDate]);

  const handleResetClick = useCallback(() => {
    setStartDate('');
    setEndDate('');

    onReset && onReset();
  }, [onReset]);

  return (
    <>
      <Stack
        ref={anchorRef}
        sx={{
          backgroundColor: 'linear-gradient(to bottom, #F4F6F8 0%, #F4F6F8 100%)',
        }}
      >
        <Button
          color="inherit"
          endIcon={<AssetIcon src="search-lg" size="m" sx={{ bgcolor: palette.primary.main }} />}
          onClick={handleClick}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            '&:hover': { backgroundColor: 'inherit' },
          }}
        >
          <Typography
            sx={{ fontSize: '14px', lineHeight: '24px', color: '#637381', fontWeight: '600' }}
          >
            {title ? title : ''}
          </Typography>
        </Button>
      </Stack>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        sx={{ marginTop: 1 }}
      >
        <Stack sx={{ width: '209px', height: 'auto', padding: '8px', gap: '8px' }}>
          <TextField
            size="small"
            label="Start Date"
            type="date"
            data-at-text-field="StartDateFilter"
            value={startDate}
            onChange={handleStartDateChange}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            label="End Date"
            type="date"
            data-at-text-field="EndDateFilter"
            value={endDate}
            onChange={handleEndDateChange}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <Stack sx={{ flexDirection: 'row', justifyContent: 'center', marginTop: '8px' }}>
            <Button
              size="small"
              variant="contained"
              data-at-button="Apply"
              onClick={handleApplyClick}
            >
              Apply
            </Button>
            <Button
              size="small"
              variant="text"
              color="inherit"
              data-at-button="Reset"
              onClick={handleResetClick}
            >
              Reset
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
};
